/**
 * THE PLACE REGISTRY — the step or tab a screen shows now, the ones it offers, which reported fields are empty, and how
 * to move to another (owner request 2026-10-17; `packages/domain/src/help/page-registry.ts`).
 *
 * A screen with steps or tabs (the new-agent wizard, an agent's page) registers through `useHelpPlace`; the portal's
 * host API reads the latest registration into the page context and, when the person presses a "Go to {step}" card the
 * help host proposed, calls `move(place)` — the screen's own step or tab change, nothing else. Codes only: never a value,
 * never a record id. No network: like `../fill/**`, this module reaches nothing outside the page.
 *
 * One screen at a time: the latest registration wins and an unregistration only removes its own.
 */
export interface HelpPlaceState {
  readonly code: string;
  readonly filled: boolean;
}

export interface HelpPlaceEntry {
  /** The step or tab shown now (a code of the page's registry entry). */
  readonly place: string;
  /** The steps or tabs the screen offers now, in order. */
  readonly placeList: readonly string[];
  /** Which reported fields are empty or filled. */
  readonly state: readonly HelpPlaceState[];
  /** Shows another step or tab — the screen's own change. */
  readonly move: (place: string) => void;
}

export interface HelpPlaceRegistry {
  register(entry: HelpPlaceEntry): () => void;
  current(): HelpPlaceEntry | null;
  /** Moves to `place` when the screen shown offers it; whether it did. */
  move(place: string): boolean;
  subscribe(listener: () => void): () => void;
}

export function createHelpPlaceRegistry(): HelpPlaceRegistry {
  const listener = new Set<() => void>();
  let latest: { readonly entry: HelpPlaceEntry; readonly token: symbol } | null = null;
  const notify = (): void => {
    for (const one of [...listener]) one();
  };
  return {
    register(entry) {
      const token = Symbol("place");
      latest = { entry, token };
      notify();
      return () => {
        if (latest?.token !== token) return;
        latest = null;
        notify();
      };
    },
    current: () => latest?.entry ?? null,
    move(place) {
      const entry = latest?.entry;
      if (entry === undefined || !entry.placeList.includes(place)) return false;
      entry.move(place);
      return true;
    },
    subscribe(one) {
      listener.add(one);
      return () => {
        listener.delete(one);
      };
    },
  };
}
